import { type SubstepType } from "~/features/configurator/Linear/dtos";
import type { TranslationModel } from "~/common/types/TranslationModel";
import type { ResolvedCsticDefinition } from "~/features/configurator/common/v2/StepDefinition";
import { KmatType } from "~/features/configurator/Linear/kmatType";

export interface LinearConfiguration {
  configId: string;
  productCode: string;
  steps: LinearStep[];
  substeps: Dict<LinearSubstep>;
  kmats: Record<KmatType, Kmat[]>;
  systemLayoutHelpTile: CustomTile | null;
  minLength: number | null;
  maxLength: number | null;
  allowedSteps: boolean[][];
  nextSteps?: (number | null)[];
  previousSteps?: (number | null)[];
  isConfigComplete: boolean;
  manufacturerLogo: string | null;
  step5PdfLink: string | null;
  currentStep: number;
  highestVisitedStep: number;
  schaefflerId: string;
  guidewayPieceLimits: Dict<{ min: number; max: number }>;
  mediaUrl: string;

  designation: string;
  description: string;
  specialTranslations: Dict<string>;
  advancedSpecialTranslations: Dict<TranslationModel>;

  cadUrl: string;
  cadXmlRequest: string;
  enableCarriageEdit?: boolean[];
  customSegmentLengthSelected: boolean | null;

  priceReference: string;

  conflict?: boolean;
  conflictedCstics: ConflictKmat[];
}

export interface Kmat {
  id: string;
  parentId?: string;
  type: KmatType;
  cstics: Dict<LinearCstic>;
}

export interface LinearStep {
  active: boolean;
  substeps: SubstepType[];
  defaultValues: { [csticName: string]: string | null }; // Values to set when resetting the entire step
  resetLabel: string;
  showResetConfirmation: boolean;
  name: string;
}

export const AdditionalDataKeys = {
  FooterType: "footerInfoType",
  TitleFontSize: "titleFontSize",
};

export type SubstepCstics = {
  helper: Record<string, ResolvedCsticDefinition>;
  input: Record<string, ResolvedCsticDefinition>;
};

export type LinearSubstep = MainKmatSubstep | SubKmatSubstep;

export type MainKmatSubstep = BaseLinearSubstep & {
  kmatType: KmatType.Main;
  cstics: SubstepCstics;
};

export type SubKmatSubstep = BaseLinearSubstep & {
  kmatType: KmatType.SubCarriage | KmatType.SubRail;
  cstics: SubstepCstics[];
};

export interface BaseLinearSubstep {
  type: SubstepType;

  // Labels start
  name: string;
  description: string;
  info: string;
  footerInfo: string[];
  extraTexts: Record<string, string>;

  tiles: SubstepTile[];
  groupedTiles: TileGroup[];

  // cstics: SubstepCstics | SubstepCstics[];

  tilesPerRow: number;
  selectedTile: number | null; // In case substep is tile based, index of selected tile
  value: string | number | null | boolean; // In case substep is not tile based, inputted value
  isInvalid?: boolean;

  /**
   * Less restrictive invalid state, used with input fields that are not yet dirty
   */
  isLenientInvalid?: boolean;
  additionalValidations: Record<string, boolean>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalData: Record<string, any>;
  isVisible: boolean;

  extraConfig: Record<string, unknown>;
}

export type SubstepTileExtended = SubstepTile & {
  onClick: () => void;
  tileIndex: number;
};

export interface TileGroup {
  name: string;
  description: string;
  info: string;
  tiles: SubstepTile[];
}

export interface SubstepTile {
  id?: string;
  name: string;
  description: string;
  info: string;
  imageUrl: string;
  cstics: { [csticName: string]: string | null }; // Map of cstics to set when selecting this tile & used to decide if tile is selected
  csticsToSetOnly?: { [csticName: string]: string | null }; // Map of cstics to set when selecting this tile but NOT used to decide if tile is selected
  csticsToEnable: { [csticName: string]: string | number }; // Map of cstics to check when determining if the tile is enabled
  isDisabledExternally: boolean;
}

export interface LinearCstic {
  key: string;
  groupId: string;
  parentGroupId?: string;
  name: string;
  label: string;
  values: LinearCsticValue[];
  value: string | undefined;
  type: string;
  info: string;
  description: string;
  invalid?: boolean;
  readonly: boolean;
  fake: boolean;
}
export interface LinearCsticValue {
  key: string;
  label: string;
  value: string;
  info: string;
  imageUrl: string;
  standard?: boolean;
}

export interface CustomTile {
  label: string;
  imageUrl: string;
  linkUrl: string;
}

export interface ConflictKmat {
  id: string;
  name: string;
  cstics: ConflictCstic[];
}

export interface ConflictCstic {
  key: string;
  name: string;
}

export interface SavedConfiguration {
  useXmlTranslation: boolean; // TODO - add proper configurator type
  code: string;
  designation: string;
  mediaUrl: string;
  name: string;
  schaefflerId: string;
  shortDescription: string;
  manufacturerUrl: string | null;
  isSelected: boolean;
  comment: string;
  showComment: boolean;
  importUrlPrefix: string | null;
}

export type CsticId = {
  key: string;
  index: number;
  type: KmatType;
};

export function SimpleCsticId(key: string, type: KmatType = KmatType.Main): CsticId {
  return { key, index: 0, type };
}

export function FullCsticId(data: { key: string; index: number; type: KmatType }): CsticId {
  return data;
}

export function FullCsticIdFromResolvedDefinition(def: ResolvedCsticDefinition): CsticId {
  return {
    index: def.kmatIndex,
    type: def.kmatType,
    key: def.name,
  };
}
